// FIGYELEM-DIALÓGUS: a hozzárendelés-lista figyelem-paneljének „Megnézem" gombja nyitja — a hamarosan induló,
// még FÜGGŐ hozzárendeléseket sorolja fel (ügyfél/fiók · szolgáltatás/konstrukció · induló dátum · hátralévő
// nap), soronként a MEGLÉVŐ szerkesztő-flow-ba mutató „Szerkesztés" akcióval (a lista `?edit=` deep-linkje).
import { type ServiceAssignmentPendingStart } from '@atlas/contracts';
import { Link } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { EmptyState } from '@/components/shared/empty-state';
import { servicePlanLabel } from '@/lib/service-plan-label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ServiceAssignmentAttentionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: ServiceAssignmentPendingStart[];
  /** A sor „Szerkesztés" akciója — a hívó nyitja meg a MEGLÉVŐ szerkesztő-sheetet (a `?edit=` deep-linken át). */
  onEdit: (id: string) => void;
}

/** `daysUntilStart` → lokalizált felirat: 0 = ma, negatív = már esedékes (X napja), pozitív = X nap múlva. */
function daysLabel(t: TFunction, days: number): string {
  if (days === 0) return t('serviceAssignments.attention.daysToday');
  if (days < 0) return t('serviceAssignments.attention.daysOverdue', { count: Math.abs(days) });
  return t('serviceAssignments.attention.daysIn', { count: days });
}

export function ServiceAssignmentAttentionDialog({
  open,
  onOpenChange,
  items,
  onEdit,
}: ServiceAssignmentAttentionDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-xl sm:w-full">
        <DialogHeader>
          <DialogTitle>{t('serviceAssignments.attention.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('serviceAssignments.attention.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="px-0 py-0">
          {items.length === 0 ? (
            <div className="p-6">
              <EmptyState title="—" />
            </div>
          ) : (
            <CardRows>
              {items.map((item) => {
                const startDate = new Date(item.startDate);
                const startDateLabel = Number.isNaN(startDate.getTime())
                  ? item.startDate
                  : startDate.toLocaleDateString(i18n.language);
                return (
                  <CardRow key={item.id}>
                    <div className="flex items-center justify-between gap-3 px-6 py-3">
                      <div className="min-w-0 space-y-0.5">
                        <div className="text-foreground truncate text-sm font-medium">
                          {item.clientName ?? '—'}
                          {item.accountName && (
                            <span className="text-muted-foreground font-normal">
                              {' '}
                              · {item.accountName}
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground truncate text-xs">
                          {servicePlanLabel(item.serviceName, item.planName) ?? '—'}
                        </div>
                        <div className="text-muted-foreground text-xs tabular-nums">
                          {startDateLabel} · {daysLabel(t, item.daysUntilStart)}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => onEdit(item.id)}
                      >
                        {t('serviceAssignments.edit')}
                      </Button>
                    </div>
                  </CardRow>
                );
              })}
            </CardRows>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" asChild>
            <Link
              to="/service-assignments"
              search={(prev: Record<string, unknown>) => ({ ...prev, status: 'pending' })}
              onClick={() => onOpenChange(false)}
            >
              {t('serviceAssignments.attention.footerLink')}
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
